import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Card, CardBody } from '../components/Card';
import { Alert } from '../components/Alert';
import { auditAPI } from '../api/auditAPI';

const ACTION_LABELS = {
    create: 'Created',
    update: 'Updated',
    delete: 'Deleted',
    status_change: 'Changed status of',
    migrate_to_leader: 'Promoted to leader',
    demote_to_member: 'Demoted to member',
    mark_absent: 'Marked absent',
    mark_present: 'Marked present',
    update_reason: 'Updated absence reason for',
    clear_email: 'Cleared email for',
    regenerate_invite: 'Regenerated invite for',
};

const ENTITY_LABELS = {
    member: 'Member',
    leader: 'Leader',
    region: 'Region',
    prayer_request: 'Prayer Request',
    attendance: 'Attendance',
};

const ENTITY_TYPE_OPTIONS = [
    { value: '', label: 'All types' },
    { value: 'member', label: 'Members' },
    { value: 'leader', label: 'Leaders' },
    { value: 'region', label: 'Regions' },
    { value: 'prayer_request', label: 'Prayer Requests' },
    { value: 'attendance', label: 'Attendance' },
];

function relativeTime(dateStr) {
    const now = new Date();
    const date = new Date(dateStr);
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);

    if (diffSec < 60) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHr < 24) return `${diffHr}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
}

function formatAction(entry) {
    const label = ACTION_LABELS[entry.action] || entry.action;
    const entityLabel = ENTITY_LABELS[entry.entity_type] || entry.entity_type;
    return `${label} ${entityLabel}`;
}

function DetailsRow({ details }) {
    if (!details || Object.keys(details).length === 0) return null;

    const items = Object.entries(details).filter(([, v]) => v !== '' && v !== null && v !== undefined);
    if (items.length === 0) return null;

    return (
        <div className="mt-2 text-xs text-slate-500 dark:text-slate-400 space-y-0.5">
            {items.map(([key, value]) => (
                <div key={key}>
                    <span className="font-medium">{key.replace(/_/g, ' ')}:</span>{' '}
                    {Array.isArray(value) ? value.join(', ') : String(value)}
                </div>
            ))}
        </div>
    );
}

export function AuditLog() {
    const { isAuthenticated } = useAuth();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [entityType, setEntityType] = useState('');
    const [expandedId, setExpandedId] = useState(null);

    const perPage = 50;

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, per_page: perPage };
            if (entityType) params.entity_type = entityType;
            const res = await auditAPI.getLogs(params);
            const data = res.data.data;
            setLogs(data.items || []);
            setTotalPages(data.total_pages || 1);
            setTotal(data.total || 0);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load audit log');
        } finally {
            setLoading(false);
        }
    }, [page, entityType]);

    useEffect(() => {
        if (isAuthenticated) fetchLogs();
    }, [isAuthenticated, fetchLogs]);

    const handleEntityTypeChange = (e) => {
        setEntityType(e.target.value);
        setPage(1);
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold text-slate-900 dark:text-white">Audit Log</h1>
                <span className="text-sm text-slate-500 dark:text-slate-400">{total} entries</span>
            </div>

            {error && <Alert variant="error">{error}</Alert>}

            {/* Filters */}
            <Card>
                <CardBody className="py-3">
                    <div className="flex flex-wrap items-center gap-3">
                        <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Filter by:</label>
                        <select
                            value={entityType}
                            onChange={handleEntityTypeChange}
                            className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm px-3 py-1.5 text-slate-700 dark:text-slate-200 focus:ring-2 focus:ring-accent-500/30 focus:border-accent-500"
                        >
                            {ENTITY_TYPE_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </select>
                    </div>
                </CardBody>
            </Card>

            {/* Table */}
            <Card>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-slate-200 dark:border-slate-700">
                                <th className="text-left px-4 py-3 font-medium text-slate-500 dark:text-slate-400">When</th>
                                <th className="text-left px-4 py-3 font-medium text-slate-500 dark:text-slate-400">Who</th>
                                <th className="text-left px-4 py-3 font-medium text-slate-500 dark:text-slate-400">Action</th>
                                <th className="text-left px-4 py-3 font-medium text-slate-500 dark:text-slate-400">Entity</th>
                                <th className="text-left px-4 py-3 font-medium text-slate-500 dark:text-slate-400 w-8"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-4 py-8 text-center text-slate-400">Loading...</td>
                                </tr>
                            ) : logs.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-4 py-8 text-center text-slate-400">No audit log entries found</td>
                                </tr>
                            ) : (
                                logs.map((entry) => (
                                    <React.Fragment key={entry.id}>
                                        <tr
                                            className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer"
                                            onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                                        >
                                            <td className="px-4 py-3 text-slate-500 dark:text-slate-400 whitespace-nowrap" title={new Date(entry.created_at).toLocaleString()}>
                                                {relativeTime(entry.created_at)}
                                            </td>
                                            <td className="px-4 py-3 text-slate-900 dark:text-white font-medium whitespace-nowrap">
                                                {entry.actor_name}
                                            </td>
                                            <td className="px-4 py-3 text-slate-700 dark:text-slate-300 whitespace-nowrap">
                                                {formatAction(entry)}
                                            </td>
                                            <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                                                {entry.entity_name || entry.entity_id}
                                            </td>
                                            <td className="px-4 py-3 text-slate-400">
                                                {entry.details && Object.keys(entry.details).length > 0 && (
                                                    <svg className={`w-4 h-4 transition-transform ${expandedId === entry.id ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <polyline points="6 9 12 15 18 9" />
                                                    </svg>
                                                )}
                                            </td>
                                        </tr>
                                        {expandedId === entry.id && entry.details && Object.keys(entry.details).length > 0 && (
                                            <tr className="bg-slate-50 dark:bg-slate-800/30">
                                                <td colSpan="5" className="px-4 py-3">
                                                    <DetailsRow details={entry.details} />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                        Page {page} of {totalPages}
                    </span>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={page <= 1}
                            className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={page >= totalPages}
                            className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
